import { Box, Typography, Paper, Button, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

const InventoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const apiUrl = "https://oven-backend.onrender.com";


  useEffect(() => {
    const fetchInventoryItem = async () => {
      try {
        const response = await axios.get(`${apiUrl}/inventory/${id}`);
        const data = response.data;
        
        // معالجة البيانات المستلمة
        const formattedData = {
          ...data,
          supplier: data.supplier?.name || t("not_available")
        };
        
        setInventory(formattedData);
      } catch (error) {
        console.error(t("error_fetching_inventory"), error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventoryItem();
  }, [id, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!inventory) {
    return <Typography variant="h6">{t("inventory_not_found")}</Typography>;
  }

  return (
    <Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        📦 {t("inventory_details")} - {inventory.itemName}
      </Typography>

      <Box 
        component={Paper} 
        elevation={3} 
        p={3} 
        mb={3}
        sx={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 2fr))",
          gap: 2,
          backgroundColor: colors.primary[400]
        }}
      >
        <DetailItem label={t("item_name")} value={inventory.itemName} />
        <DetailItem label={t("total_quantity")} value={inventory.totalQuantity} />
        <DetailItem label={t("min_stock_level")} value={inventory.minStockLevel} />
        <DetailItem label={t("unit_price")} value={`$${inventory.unitPrice?.toFixed(2)}`} />
        <DetailItem label={t("supplier_name")} value={inventory.supplier} />
        <DetailItem label={t("last_purchase_date")} value={new Date(inventory.lastPurchaseDate).toLocaleDateString()} />
      </Box>

      <Box 
        component={Paper} 
        elevation={3} 
        p={2} 
        mb={3}
        sx={{ backgroundColor: colors.greenAccent[500] }}
      >
        <Typography variant="h6">
          {t("total_stock_value")}: <b>${(inventory.totalQuantity * inventory.unitPrice).toFixed(2)}</b>
        </Typography>
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/updateInventory/${id}`)}
          sx={{ flex: '1 1 200px',display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("edit_item")}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ flex: '1 1 200px', display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("print")}
        </Button>

        <Button
          variant="contained"
          color="success"
          startIcon={<PictureAsPdfIcon />}
          sx={{ flex: '1 1 200px',display: "flex", alignItems: "center", gap: "8px" }}
        >
          {t("export_pdf")}
        </Button>
      </Box>
    </Box>
  );
};

// مكون مساعد لعرض التفاصيل
const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" color="textSecondary">{label}:</Typography>
    <Typography variant="body1" fontWeight="500">{value || "N/A"}</Typography>
  </Box>
);

export default InventoryDetails;
